/** @jsx jsx */
import React, { Fragment } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Box, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import type { ItemProviderDataSection } from '../../types/item-provider.tsx';
import Item from './item/index.tsx';
import { hasItems } from './utils.tsx';

const styles = cssMap({
	sectionHeader: {
		color: token('color.text.subtlest'),
		marginTop: token('space.100'),
		font: token('font.heading.xxsmall'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		marginTop: token('space.200'),
	},
	sectionHeaderInnerContainer: {
		color: token('color.text.subtlest'),
		font: token('font.heading.xxsmall'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
	},
});

type Props = {
	sections: ItemProviderDataSection[];
	showStar?: boolean;
	onItemClick?: () => void;
};

const ItemList = ({ sections, showStar, onItemClick }: Props): JSX.Element => (
	<div css={styles.list} data-testid="global-pages.home.common.ui.item-list.list">
		{sections.filter(hasItems).map((section, i) => {
			const { header = '' } = section;

			return (
				<Fragment key={`${header}-${i}`}>
					{header ? (
						// styles are applied to all items except the first one so that we
						// don't have unnecessary margin for the first item in order to match
						// the look and feel of original version
						<Box xcss={i > 0 ? sectionHeaderContainerStyles : null}>
							<Heading as="h3" size="xxsmall">
								<span css={styles.sectionHeaderInnerContainer}>{header}</span>
							</Heading>
						</Box>
					) : null}
					<Box as="ul" xcss={listContainerStyles}>
						{section.items.map((item, index) => (
							<Box as="li" key={item.url}>
								<Item item={item} index={index} showStar={showStar} onItemClick={onItemClick} />
							</Box>
						))}
					</Box>
				</Fragment>
			);
		})}
	</div>
);

const sectionHeaderContainerStyles = xcss({
	marginTop: 'space.100',
});

const listContainerStyles = xcss({
	padding: 'space.0',
	listStyleType: 'none',
	marginTop: 'space.0',
});

export default ItemList;
